//@ts-nocheck
import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Typography } from '@mui/material';
import { Sparkle, UploadSimple, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { XGButton, XGChip, XGInput } from 'xg-cl';
import * as PropTypes from 'prop-types';
import ProseTextarea from '../../../ProseTextarea/ProseTextarea';
import { isFileTypeAndExtensionAllowed } from '../../../../utils/fileValidation';
import './TripImportSection.css';

const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

const isValidHttpUrl = (candidate) => {
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (err) {
    return false;
  }
};

/**
 * B2 "Have something already?" section shown below the standard create-trip
 * fields. Controlled: the parent owns `value` and receives the merged
 * `{ text, file, url }` object on every change.
 *
 * @param {Object} props
 * @param {{text: string, file: File|null, url: string}} props.value
 * @param {(value: {text: string, file: File|null, url: string}) => void} props.onChange
 * @param {boolean} [props.allowDocuments] - Enables PDF/Word picker and drop handling.
 * @param {boolean} [props.disabled]
 * @param {string} [props.errorCode] - A `TripImportErrorCode` to show inline, if any.
 * @param {boolean} [props.showTextPrompt] - The free-text prompt is intentionally
 *   hidden for now (the box takes a link or a PDF/Word file). Pass `true` to
 *   bring it back; the parent's `text` state and payload already support it.
 * @param {(status: 'empty'|'valid'|'invalid') => void} [props.onLinkStatusChange] -
 *   Reports whether the link input is empty, holds a valid http(s) URL, or holds
 *   text that isn't one yet (even before it is committed on blur).
 * @param {() => void} [props.onLogin] - Shown as a "Log in" button next to a session-expired error.
 */
const TripImportSection = ({
  value,
  onChange,
  disabled,
  errorCode,
  onLogin,
  onLinkStatusChange,
  showTextPrompt,
  allowDocuments
}) => {
  const { t } = useTranslation(['trip']);
  const isSessionError =
    errorCode === 'SESSION_EXPIRED' || errorCode === 'COMMIT_SESSION_EXPIRED';
  // The site refused our fetch (bot protection, login wall), so offer pasting
  // the page text even though the text prompt is normally hidden.
  const isLinkBlocked = errorCode === 'URL_ACCESS_DENIED';
  // The parent clears the error code on the first edit, so remember that the
  // paste box was offered; otherwise it would vanish after one keystroke.
  const [fallbackTextRevealed, setFallbackTextRevealed] = useState(false);
  useEffect(() => {
    if (isLinkBlocked) {
      setFallbackTextRevealed(true);
    }
  }, [isLinkBlocked]);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [fileError, setFileError] = useState('');

  const { text = '', file = null, url = '' } = value || {};
  const [linkDraft, setLinkDraft] = useState(url);
  const linkInputRef = useRef(null);

  // Keep the displayed link in sync with `value.url` (e.g. a file replacing
  // the link, or the parent restoring state after Cancel/error).
  useEffect(() => {
    setLinkDraft(url);
  }, [url]);

  const trimmedDraft = linkDraft.trim();
  let linkStatus = 'invalid';
  if (!trimmedDraft) {
    linkStatus = 'empty';
  } else if (isValidHttpUrl(trimmedDraft)) {
    linkStatus = 'valid';
  }

  useEffect(() => {
    onLinkStatusChange?.(linkStatus);
  }, [linkStatus, onLinkStatusChange]);

  const hasContent = Boolean(text.trim() || file || url);

  const updateValue = (patch) => {
    onChange?.({ text, file, url, ...patch });
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const applyFile = (candidate) => {
    if (!allowDocuments || disabled || !candidate) {
      return;
    }

    if (candidate.size > MAX_FILE_SIZE) {
      setFileError(t('tripImport.errors.fileTooLarge'));
      clearFileInput();
      return;
    }

    if (
      !isFileTypeAndExtensionAllowed(
        candidate,
        ACCEPTED_MIME_TYPES,
        ACCEPTED_EXTENSIONS
      )
    ) {
      setFileError(t('tripImport.errors.unsupportedFile'));
      clearFileInput();
      return;
    }

    setFileError('');
    setLinkDraft('');
    setLinkError('');
    // Always clear the native input's value, even on a successful pick:
    // otherwise re-selecting the *same* file later (e.g. after Cancel/error
    // brings the operator back here with that file still attached, or in a
    // demo re-using the same sample file) leaves the input's file list
    // unchanged, so the browser never fires another `change` event and the
    // attach silently does nothing until a hard refresh.
    clearFileInput();
    updateValue({ file: candidate, url: '' });
  };

  const handleFileInputChange = (evt) => {
    applyFile(evt.target.files?.[0]);
  };

  const handleRemoveFile = () => {
    setFileError('');
    clearFileInput();
    updateValue({ file: null });
  };

  const handleDragOver = (evt) => {
    evt.preventDefault();
  };

  const handleDragEnter = (evt) => {
    evt.preventDefault();
    if (disabled || !allowDocuments) {
      return;
    }
    setIsDragging(true);
  };

  const handleDragLeave = (evt) => {
    if (!evt.currentTarget.contains(evt.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (evt) => {
    evt.preventDefault();
    setIsDragging(false);
    if (disabled || !allowDocuments) {
      return;
    }
    applyFile(evt.dataTransfer.files?.[0]);
  };

  const commitLink = () => {
    const trimmed = linkDraft.trim();

    if (!trimmed) {
      // Clearing the field and leaving it removes the link.
      setLinkError('');
      if (url) {
        updateValue({ url: '' });
      }
      return;
    }

    if (!isValidHttpUrl(trimmed)) {
      setLinkError(t('tripImport.errors.invalidLink'));
      return;
    }

    setLinkError('');
    setLinkDraft(trimmed);
    if (trimmed !== url || file) {
      setFileError('');
      clearFileInput();
      updateValue({ url: trimmed, file: null });
    }
  };

  const handleLinkKeyDown = (evt) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      commitLink();
    }
  };

  const handleRemoveLink = () => {
    setLinkError('');
    setLinkDraft('');
    updateValue({ url: '' });
  };

  return (
    <Box className='trip-import-section'>
      <Box
        className={`trip-import-box${hasContent ? ' trip-import-box--filled' : ''}${
          allowDocuments && isDragging ? ' trip-import-box--dragging' : ''
        }`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Box className='trip-import-agent-header'>
          <Box className='trip-import-agent-header-icon-label'>
            <Sparkle
              size='1em'
              weight='fill'
              className='trip-import-agent-icon'
            />
            <span className='trip-import-agent-label'>
              {t('tripImport.agentLabel')}
            </span>
          </Box>
          <span className='trip-import-agent-subtitle'>
            {t(
              allowDocuments
                ? 'tripImport.agentSubtitle'
                : 'tripImport.agentSubtitleLinkOnly'
            )}
          </span>
        </Box>

        {allowDocuments && isDragging && (
          <Box className='trip-import-drop-hint' aria-hidden='true'>
            {t('tripImport.dropHint')}
          </Box>
        )}

        <Box className='trip-import-link-input'>
          <XGInput
            id='trip-import-link-input'
            name='tripImportLink'
            type='url'
            label={t('tripImport.linkLabel')}
            placeholder={t('tripImport.linkPlaceholder')}
            value={linkDraft}
            error={Boolean(linkError)}
            hint={linkError}
            disabled={disabled}
            inputRef={linkInputRef}
            onChange={(evt) => {
              setLinkDraft(evt.target.value);
              if (linkError) {
                setLinkError('');
              }
            }}
            onKeyDown={handleLinkKeyDown}
            onBlur={commitLink}
            endAdornment={
              linkDraft && !disabled ? (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={t('tripImport.removeLink')}
                    size='small'
                    // Keep focus in the input so blur doesn't re-commit
                    // before the removal runs.
                    onMouseDown={(evt) => evt.preventDefault()}
                    onClick={handleRemoveLink}
                  >
                    <X size={14} />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }
          />
        </Box>

        {allowDocuments && (
          <>
            <Box className='trip-import-divider'>
              <span>{t('tripImport.dividerLabel')}</span>
            </Box>

            <button
              type='button'
              id='trip-import-attach-file-button'
              className='trip-import-dropzone'
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadSimple size={24} />
              <span className='trip-import-dropzone-title'>
                {t('tripImport.attachFile')}
              </span>
              <span className='trip-import-dropzone-hint'>
                {t('tripImport.dropzoneHint')}
              </span>
            </button>

            {file && (
              <Box className='trip-import-attachment-chip'>
                <XGChip
                  id='trip-import-file-chip'
                  text={file.name}
                  type='default'
                  variant='outlined'
                  size='medium'
                />
                {!disabled && (
                  <IconButton
                    aria-label={t('tripImport.removeFile')}
                    size='small'
                    onClick={handleRemoveFile}
                  >
                    <X size={14} />
                  </IconButton>
                )}
              </Box>
            )}

            <input
              ref={fileInputRef}
              type='file'
              accept='.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              aria-label={t('tripImport.attachFile')}
              disabled={disabled}
            />
          </>
        )}

        {(showTextPrompt || isLinkBlocked || fallbackTextRevealed) && (
          <ProseTextarea
            id='trip-import-textarea'
            aria-label={t(
              allowDocuments
                ? 'tripImport.agentSubtitle'
                : 'tripImport.agentSubtitleLinkOnly'
            )}
            placeholder={t('tripImport.placeholder')}
            value={text}
            minRows={4}
            maxRows={8}
            disabled={disabled}
            onChange={(evt) =>
              // A blocked link would just fail again (with text alongside a
              // link, the text is only treated as notes), so drop it.
              updateValue(
                isLinkBlocked
                  ? { text: evt.target.value, url: '' }
                  : { text: evt.target.value }
              )
            }
            sx={{ width: '100%' }}
          />
        )}

        <Typography variant='caption' className='trip-import-helper-text'>
          {t('tripImport.helperText')}
        </Typography>

        {fileError && (
          <Typography
            variant='caption'
            className='trip-import-error-text'
            role='alert'
          >
            {fileError}
          </Typography>
        )}

        {errorCode && (
          <Box
            className='trip-import-error-row'
            role='alert'
            data-error-code={errorCode}
          >
            <Typography variant='caption' className='trip-import-error-text'>
              {t(`tripImport.errors.${errorCode}`, {
                defaultValue: t('tripImport.errors.INTERNAL')
              })}
            </Typography>
            {isSessionError && onLogin && (
              <XGButton
                id='trip-import-login-button'
                text={t('tripImport.logIn')}
                type='primary'
                variant='contained'
                size='small'
                onClick={onLogin}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

TripImportSection.propTypes = {
  value: PropTypes.shape({
    text: PropTypes.string,
    file: PropTypes.object,
    url: PropTypes.string
  }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errorCode: PropTypes.string,
  onLogin: PropTypes.func,
  onLinkStatusChange: PropTypes.func,
  showTextPrompt: PropTypes.bool,
  allowDocuments: PropTypes.bool
};

TripImportSection.defaultProps = {
  value: { text: '', file: null, url: '' },
  disabled: false,
  errorCode: '',
  onLogin: undefined,
  onLinkStatusChange: undefined,
  showTextPrompt: false,
  allowDocuments: false
};

export default TripImportSection;
